/**
 * Registro del service worker (`public/sw.js`).
 *
 * Habilita la instalación como PWA en Android/Chrome, que exige un SW con
 * handler de `fetch` además del manifest. El SW no cachea JS/CSS a propósito:
 * ver el comentario de cabecera en `public/sw.js`.
 *
 * Sólo en producción y sobre HTTPS. En dev sólo desregistra lo que haya
 * quedado, para que un SW viejo nunca se interponga con el HMR de Vite.
 */

declare global {
  interface Window {
    /** Escotilla de emergencia expuesta en `main.tsx`. */
    __unregisterSW?: () => Promise<void>;
  }
}

const SW_URL = "/sw.js";

export function registerServiceWorker(): void {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

  // localhost cuenta como secure context, pero igual no queremos SW en dev.
  if (import.meta.env.DEV) {
    void unregisterServiceWorker();
    return;
  }

  if (!window.isSecureContext) return;

  window.addEventListener("load", () => {
    navigator.serviceWorker.register(SW_URL).catch(() => {
      /* SW opcional: sin él la app funciona igual, sólo no es instalable */
    });
  });
}

/** Kill switch — desde consola: `window.__unregisterSW?.()`. */
export async function unregisterServiceWorker(): Promise<void> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      regs.map((reg) => {
        reg.active?.postMessage("UNREGISTER");
        return reg.unregister();
      }),
    );
  } catch {
    /* nada que hacer si el navegador lo bloquea */
  }
}
